import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import TechBackground from '../components/TechBackground';

const ServicesPage = () => {
  const [servicesRef, servicesInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [processRef, processInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [faqRef, faqInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const services = [
    {
      icon: '🔮',
      title: 'Web Development',
      description: 'Custom, responsive websites that convert visitors into customers with modern UI/UX principles.',
      features: ['Responsive Design', 'SEO Optimization', 'Performance Tuning', 'Custom CMS Integration']
    },
    {
      icon: '🤖',
      title: 'AI Integration',
      description: 'Leverage the power of artificial intelligence to automate processes and enhance your business.',
      features: ['Machine Learning Models', 'Natural Language Processing', 'Predictive Analytics', 'Custom AI Solutions']
    },
    {
      icon: '✨',
      title: 'Branding & Design',
      description: 'Create a memorable brand identity that resonates with your audience and stands out.',
      features: ['Logo Design', 'Brand Identity', 'Style Guidelines', 'Marketing Collateral']
    },
    {
      icon: '💫',
      title: 'Digital Advertising',
      description: 'Strategic digital campaigns that drive growth and engagement with your target audience.',
      features: ['PPC Campaign Management', 'Social Media Advertising', 'Retargeting Strategies', 'Performance Analytics']
    },
    {
      icon: '⚡',
      title: 'IT Development & Maintenance',
      description: 'Comprehensive IT solutions with ongoing support and maintenance for your business.',
      features: ['Legacy System Migration', 'Software Maintenance', 'Security Updates', 'Performance Monitoring']
    },
    {
      icon: '🌟',
      title: 'SaaS Development',
      description: 'Custom software-as-a-service solutions that scale with your business needs.',
      features: ['Multi-tenant Architecture', 'Subscription Management', 'API Development', 'Cloud Deployment']
    }
  ];

  const workProcess = [
    {
      number: '01',
      title: 'Discovery',
      description: 'We start by understanding your business goals, target audience, and project requirements.',
      icon: '🔍'
    },
    {
      number: '02',
      title: 'Strategy',
      description: 'Our team develops a comprehensive strategy and detailed roadmap for your project.',
      icon: '📝'
    },
    {
      number: '03',
      title: 'Design & Development',
      description: 'We design and build your solution using the latest technologies and best practices.',
      icon: '⚙️'
    },
    {
      number: '04',
      title: 'Testing',
      description: 'Rigorous testing ensures your solution works flawlessly across all devices and scenarios.',
      icon: '🧪'
    },
    {
      number: '05',
      title: 'Deployment',
      description: 'We launch your solution and provide training to ensure a smooth transition.',
      icon: '🚀'
    },
    {
      number: '06',
      title: 'Maintenance',
      description: 'Ongoing support and updates keep your solution running optimally and secure.',
      icon: '🛡️'
    }
  ];

  const faqs = [
    {
      question: "What technologies do you use for web development?",
      answer: "We use cutting-edge technologies including React, Angular, Vue.js, Node.js, and AWS/Azure cloud services depending on your project requirements and goals."
    },
    {
      question: "How long does a typical project take to complete?",
      answer: "Project timelines vary based on complexity and requirements. A simple website might take 2-4 weeks, while complex web applications or AI solutions can take 2-6 months or more."
    },
    {
      question: "Do you provide support after the project is completed?",
      answer: "Yes, we offer comprehensive maintenance and support packages to ensure your solution remains secure, up-to-date, and running smoothly."
    },
    {
      question: "How do you handle project pricing?",
      answer: "We offer flexible pricing models including fixed-price quotes for well-defined projects and time-and-materials pricing for projects with evolving requirements."
    },
    {
      question: "Can you integrate AI with our existing systems?",
      answer: "Yes, we specialize in integrating AI capabilities with existing business systems and workflows to maximize efficiency and value."
    }
  ];

  return (
    <motion.div 
      className="services-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <motion.section 
        className="services-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="hero-background">
          <TechBackground />
        </div>
        <div className="hero-content">
          <motion.h1
            className="gradient-text"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            Innovative Technology Solutions
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            Empowering your business with cutting-edge technology and expert implementation
          </motion.p>
          <motion.div 
            className="hero-buttons"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            <motion.button
              className="cta-button"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Get Started
              <div className="button-glow"></div>
            </motion.button>
            <motion.button
              className="outline-button"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              View Case Studies
            </motion.button>
          </motion.div>
        </div>
      </motion.section>

      <motion.section 
        ref={servicesRef}
        className="services-overview"
      >
        <div className="section-header">
          <motion.h2
            className="gradient-text"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Our Services
          </motion.h2>
          <motion.p
            className="section-subtext"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Comprehensive technology solutions tailored to your business needs
          </motion.p>
        </div>

        <motion.div
          className="services-grid"
          variants={containerVariants}
          initial="hidden"
          animate={servicesInView ? "visible" : "hidden"}
        >
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              className="service-card glass-morphism"
              variants={itemVariants}
              whileHover={{ 
                y: -10,
                transition: { duration: 0.2 }
              }}
            >
              <motion.div 
                className={`service-icon service-icon-${service.title.toLowerCase().split(' ')[0]}`}
                animate={{ 
                  rotateY: [0, 360],
                  scale: [1, 1.2, 1],
                  filter: [
                    'drop-shadow(0 10px 15px rgba(56, 189, 248, 0.2))',
                    `drop-shadow(0 20px 25px rgba(${index * 20}, 189, 248, 0.4))`,
                    'drop-shadow(0 10px 15px rgba(56, 189, 248, 0.2))'
                  ]
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  repeatType: "reverse",
                  ease: "easeInOut"
                }}
              >
                <div className="icon-background" />
                {service.icon}
              </motion.div>
              <h3>{service.title}</h3>
              <p>{service.description}</p>
              <ul className="features-list">
                {service.features.map((feature, i) => (
                  <motion.li 
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4 + (i * 0.1) }}
                  >
                    {feature}
                  </motion.li>
                ))}
              </ul>
              <motion.button
                className="outline-button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Learn More
              </motion.button>
              <div className="service-glow" />
              
            </motion.div>
          ))}
        </motion.div>
      </motion.section>

      <motion.section 
        ref={processRef}
        className="work-process"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <div className="section-header">
          <motion.h2
            className="gradient-text"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Our Process
          </motion.h2>
          <motion.p
            className="section-subtext"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            A proven methodology for successful project delivery
          </motion.p>
        </div>
        
        <div className="process-timeline">
          {workProcess.map((step, index) => (
            <motion.div 
              key={step.number}
              className="process-step"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
            >
              <div className="step-content">
                <div className="step-icon-container">
                  <motion.div 
                    className="step-icon"
                    animate={{ 
                      boxShadow: ['0 0 0 rgba(56, 189, 248, 0.3)', '0 0 20px rgba(56, 189, 248, 0.6)', '0 0 0 rgba(56, 189, 248, 0.3)']
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    {step.icon}
                  </motion.div>
                  <div className="step-number">{step.number}</div>
                </div>
                <div className="step-info">
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
              </div>
              {index < workProcess.length - 1 && <div className="process-connector" />}
            </motion.div>
          ))}
        </div>
      </motion.section>

      <motion.section 
        className="services-testimonial glass-morphism"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <div className="testimonial-background">
          <div className="tech-circles">
            {[...Array(3)].map((_, i) => (
              <motion.div
                key={i}
                className="tech-circle"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ 
                  scale: [1, 1.2, 1],
                  opacity: [0.1, 0.2, 0.1]
                }}
                transition={{
                  duration: 3,
                  delay: i * 0.2,
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
              />
            ))}
          </div>
        </div>
        
        <div className="client-testimonial">
          <motion.div 
            className="quote-icon"
            initial={{ opacity: 0, scale: 0 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            "
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Working with OasisLogic transformed our business operations. Their AI integration services helped us automate key processes and gain valuable insights from our data. The team's expertise and professionalism exceeded our expectations.
          </motion.p>
          <motion.div 
            className="testimonial-author"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="author-image">
              <img src="https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&auto=format&fit=crop&w=861&q=80" alt="Emily Chen" />
            </div>
            <div className="author-details">
              <h4>Emily Chen</h4>
              <p>CTO, FutureTech Solutions</p>
            </div>
          </motion.div>
        </div>
        
        <div className="cta-content">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Ready to Get Started?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Let's discuss your project and find the perfect solution for your business
          </motion.p>
          <motion.button 
            className="cta-button"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Contact Us
            <div className="button-glow"></div>
          </motion.button>
        </div>
      </motion.section>
      
      <motion.section 
        ref={faqRef}
        className="faq-section"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <div className="section-header">
          <motion.h2
            className="gradient-text"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Frequently Asked Questions
          </motion.h2>
          <motion.p
            className="section-subtext"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Answers to common questions about our services
          </motion.p>
        </div>
        
        <div className="faq-container">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              className="faq-item glass-morphism"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              whileHover={{ scale: 1.01 }}
            >
              <motion.h3
                className="faq-question"
              >
                {faq.question}
                <motion.span 
                  className="faq-icon"
                  transition={{ duration: 0.3 }}
                >
                  ⟩
                </motion.span>
              </motion.h3>
              <motion.div 
                className="faq-answer"
                initial={{ height: "auto" }}
              >
                <p>{faq.answer}</p>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </motion.section>
    </motion.div>
  );
};

export default ServicesPage;